import { LearnerQuestion } from '../../../../../models/LearnerQuestionnaire';
import {
    BodyLarge,
    BodyRegular,
    FlexColSm,
    FlexSmAlignCenter,
    FlexSpaceBetween,
    Headline2,
} from '../../../../../components/Elements';
import strings from '../../../../../strings/strings.json';
import styled from 'styled-components';
import { breakpoints, sizes, theme } from '../../../../../theme';
import * as React from 'react';
import TextHelperIcon from '../../../../../assets/icons/indicators/TextHelperIcon';
import TextAreaField from '../../../../../components/forms/TextAreaField';
import { createErrorMessage, fieldErrorCodes, requiredFieldErrorMessage } from '../../../../../shared/error-messages';
import useNeveForm from '../../../../../components/forms/NeveForm';
import NavButton from '../../../../../components/buttons/NavButton';
import Button from '../../../../../components/buttons/Button';
import useWindowWidth from '../../../../../hooks/useWindowWidth';
import { ErrorNotifierSlim } from '../../../../../components/notifiers/ErrorNotifierSlim';

type QuestionnaireResponseInput = {
    answer: string;
};

type QuestionnaireQuestionFormProps = {
    totalQuestions: number;
    currentQuestion: LearnerQuestion | undefined;
    currentQuestionNumber: number;
    nextQuestion: (answer: string) => Promise<boolean>;
    previousQuestion: (answer: string) => Promise<boolean>;
    firstQuestion: boolean;
    lastQuestion: boolean;
};

export default function QuestionnaireQuestionForm({
    totalQuestions,
    currentQuestion,
    currentQuestionNumber,
    nextQuestion,
    previousQuestion,
    firstQuestion,
    lastQuestion,
}: QuestionnaireQuestionFormProps): JSX.Element {
    const { isMobileWidth } = useWindowWidth();
    const [submitError, setSubmitError] = React.useState<boolean>(false);
    const [isHintVisible, setIsHintVisible] = React.useState<boolean>(false);

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        watch,
        formState: { errors },
        clearErrors,
        trigger,
        setFocus,
    } = useNeveForm<QuestionnaireResponseInput>({
        answer: currentQuestion?.answer ?? '',
    });

    const watchedAnswer = watch('answer');

    React.useEffect(() => {
        setValue('answer', currentQuestion?.answer ?? '');
        clearErrors();
    }, [currentQuestion, currentQuestionNumber, setValue, clearErrors]);

    React.useEffect(() => {
        setIsHintVisible(false);
    }, [currentQuestionNumber]);

    async function next() {
        const formData = getValues();

        const success = await nextQuestion(formData.answer);
        if (!success) {
            setSubmitError(true);
        }

        setFocus('answer');
    }

    async function previous() {
        const response = await trigger('answer', { shouldFocus: true });
        if (!response) return;

        const formData = getValues();
        const success = await previousQuestion(formData.answer);

        if (!success) {
            setSubmitError(true);
        }

        if (!firstQuestion) {
            setFocus('answer');
        }
    }

    if (!currentQuestion) return <div>Loading...</div>;

    // Create unique ids for screen reader
    const headlineId = `question-${currentQuestionNumber + 1}-headline`;
    const questionId = `question-${currentQuestionNumber + 1}-text`;
    const hintId = currentQuestion.hint ? `question-${currentQuestionNumber + 1}` : undefined;

    // The hint is supplementary content and is only associated with the answer field when revealed.
    const ariaLabelledBy = [headlineId, questionId].join(' ');

    let backButtonTitle = '';
    let backButtonAria = '';

    if (firstQuestion) {
        backButtonTitle = isMobileWidth
            ? strings.questionnaireAnswerForm.backToStartButtonMobile
            : strings.questionnaireAnswerForm.backToStartButton;

        backButtonAria = strings.questionnaireAnswerForm.backToStartButtonAria;
    } else {
        backButtonTitle = isMobileWidth
            ? strings.questionnaireAnswerForm.previousQuestionButtonMobile
            : strings.questionnaireAnswerForm.previousQuestionButton;

        backButtonAria = strings.questionnaireAnswerForm.previousQuestionButtonAria;
    }

    let nextButtonTitle = '';
    let nextButtonAria = '';

    if (lastQuestion) {
        nextButtonTitle = isMobileWidth
            ? strings.questionnaireAnswerForm.completeQuestionnaireButtonMobile
            : strings.questionnaireAnswerForm.completeQuestionnaireButton;
        nextButtonAria = strings.questionnaireAnswerForm.completeQuestionnaireButtonAria;
    } else {
        nextButtonTitle = isMobileWidth
            ? strings.questionnaireAnswerForm.nextQuestionButtonMobile
            : strings.questionnaireAnswerForm.nextQuestionButton;
        nextButtonAria = strings.questionnaireAnswerForm.nextQuestionButtonAria;
    }

    return (
        <QuestionForm onSubmit={handleSubmit(next)}>
            <Headline2 id={headlineId}>
                {strings.questionnairePage.questionNumberLabel} {currentQuestionNumber + 1}/{totalQuestions}
            </Headline2>
            <FlexColSm>
                <BodyLarge $bold id={questionId}>
                    {currentQuestion.question}
                </BodyLarge>
                {currentQuestion.hint && (
                    <>
                        <Button
                            title={
                                isHintVisible
                                    ? strings.questionnaireAnswerForm.hideHintButton
                                    : strings.questionnaireAnswerForm.showHintButton
                            }
                            aria={
                                isHintVisible
                                    ? strings.questionnaireAnswerForm.hideHintButtonAria
                                    : strings.questionnaireAnswerForm.showHintButtonAria
                            }
                            aria-expanded={isHintVisible}
                            aria-controls={hintId}
                            onClick={() => setIsHintVisible((visible) => !visible)}
                        />
                        {isHintVisible && (
                            <HelperTextContainer>
                                <TextHelperIcon colour={theme.textColour} />
                                <HelperText id={hintId}>{currentQuestion.hint}</HelperText>
                            </HelperTextContainer>
                        )}
                    </>
                )}
            </FlexColSm>
            <TextAreaField
                fieldName={`${strings.questionnaireAnswerForm.answerInputLabel}-${currentQuestionNumber + 1}`}
                aria={`${strings.questionnaireAnswerForm.answerInputLabel}-${currentQuestionNumber + 1}`}
                maxLength={2000}
                charactersLeft={2000 - watchedAnswer.length}
                required
                ariaLabelledBy={ariaLabelledBy}
                ariaDescribedBy={isHintVisible ? hintId : undefined}
                errorMessage={errors.answer?.message}
                autoFocus
                {...register('answer', {
                    required: {
                        value: true,
                        message: requiredFieldErrorMessage(strings.questionnaireAnswerForm.answerInputLabel),
                    },
                    maxLength: {
                        value: 2000,
                        message: createErrorMessage(
                            strings.questionnaireAnswerForm.answerInputLabel,
                            fieldErrorCodes.maxLength,
                        ),
                    },
                })}
            />
            {submitError && <ErrorNotifierSlim description={strings.questionnairePage.questionSubmitError} />}
            <ButtonContainer>
                <NavButton
                    title={backButtonTitle}
                    aria={backButtonAria}
                    back
                    onClick={() => previous()}
                    type="button"
                    inverted
                />
                <NavButton title={nextButtonTitle} aria={nextButtonAria} type="submit" inverted />
            </ButtonContainer>
        </QuestionForm>
    );
}

const QuestionForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};
`;

const HelperTextContainer = styled(FlexSmAlignCenter)`
    width: 100%;
    background-color: ${theme.adminSubpageHeaderColour};
    border-radius: ${sizes.borderRadiusMd};
    padding: ${sizes.spacingMd};
    @media (max-width: ${breakpoints.sm}) {
        width: 100%;
    }
`;

const HelperText = styled(BodyRegular)`
    white-space: pre-wrap;
`;

const ButtonContainer = styled(FlexSpaceBetween)`
    width: 100%;
`;
